import { create } from "zustand";
import * as AzureApi from "../Api/azure";
import _ from "lodash";
import * as Models from "../Models";
import {
  getTableColumnsByResultView,
  setColumnDefaultValues,
  getColumnSortValue,
  getPayload,
  combineResults,
  getExportPayload,
} from "../Helpers/searchHelper";
import * as searchHelpers from "../Helpers/search";
import { FileExportStatus } from "../Models";
import { useMarketAreaStore, userStore } from "./userStore";

const pageSize = 2500; // 100 000 must be divisible with pageSize

interface searchStoreState extends Models.SearchState {
  editFilter: any;
  getAdvancedSearchOptions?: () => void;
  loadRecentQuery?: (query: Models.UserSearch) => void;
  setAdvancedSearchIndex?: (newSearchIndex: string) => void;
  getRecentSearchQueries?: () => void;
  setAdvancedSearchEditMode?: () => void;
  setSearchFilter?: (payload: Models.SearchFilterPayload) => void;
  setFilters?: (state: searchStoreState, data) => void;
  hideFilters?: (field: Models.SearchSettingField) => void;
  saveHiddenFilters?: () => void;
  removeSearchFilter?: (payload: Models.SearchFilterPayload) => void;
  changeSearchFilterOperator?: (payload: Models.SearchFilterPayload) => void;
  resetAdvancedSearchFilters?: () => void;
  editSearchFilter?: (payload: Models.SearchFilterPayload) => void;
  setAdvancedSearchCancelled?: (value: boolean) => void;
  setAdvancedSearchLoading?: (value: boolean) => void;
  fetchResultCount?: (
    advancedSearchPayload: Models.AdvancedSearchApiPayload
  ) => void;
  doAdvancedSearch?: (silent?: boolean) => void;
  onToggleColumn?: (columns) => void;
  onClearGroupColumns?: (group: number) => void;
  onAddColumn?: (columns: Models.ReactTableFilter[]) => void;
  onClearClassColumns?: () => void;
  exportToExcel?: () => void;
  setAdvancedSearchTermsAndConditions?: (value: boolean) => void;
  toggleDownloadsMenu?: (show: boolean) => void;
  updateAdvancedSearchexportFile?: (
    action: Models.FileExportActionPayload
  ) => void;
  getDownloads?: () => Models.FileExport[];
}

const initialState: searchStoreState = {
  isEditMode: false,
  results: {},
  filters: {
    locationDescription: [],
    locationSiteId: [],
    marketAreas: [],
    countries: [],
    logicalFeatureTypes: [],
    salesforceLocationTypes: [],
    buildDescription: [],
    engineeringClasses: [],
    engineeringClassAttributeNames: [],
    proprietaryAttributeValues: [],
    installationDate: [],
    buildProjectId: [],
    buildId: [],
    buildConfidentiality: [],
    LocationLatitudeLongitude: [],
    buildSerialNumber: [],
    productGroupDescription: [],
    productFamilyDescription: [],
    productSeriesDescription: [],
    sellableProductModelDescription: [],
    productName: [],
    productDescription: [],
    productLifeCycleStates: [],
    productOriginalSource: [],
    productHierarchyCode: [],
    primaryCommodity: [],
    siteOperatingStatus: [],
    buildEndOfSupportDate: [],
    buildEndOfFullSupportDate: [],
    customerId: [],
    customerName: [],
    customerSalesAccountName: [],
    customerRole: [],
    customerCVCCategory: [],
    customerSiteAccountManager: [],
    locationNickname: [],
    buildLocationType: [],
    documentName: [],
    documentId: [],
    documentEffectiveDate: [],
    documentLocationSiteId: [],
    documentLocationType: [],
    documentLocationStatus: [],
    documentLocationSiteDescription: [],
    documentAuthor: [],
    documentDocTypeCode: [],
    documentIsDeliverable: [],
    documentFileName: [],
    buildOem: [],
    documentLocationInformationLifecycle: [],
    buildConstructionYear: [],
    buildDeliveryDate: [],
    buildGlobalProjectId: [],
    buildSource: [],
    buildTagNumber: [],
    buildWarrantyStartDate: [],
    locationDevelopmentState: [],
    buildApplication: [],
    buildCreatedDate: [],
    documentClassification: [],
    documentFunctionalLocationId: [],
    documentSource: [],
    locationState: [],
  },
  tableColumns: [],
  searchIndex: "",
  options: {
    locationDescription: {},
    LocationLatitudeLongitude: {},
    locationSiteId: {},
    marketAreas: {},
    countries: {},
    logicalFeatureTypes: {},
    salesforceLocationTypes: {},
    buildDescription: {},
    engineeringClasses: {},
    engineeringClassAttributeNames: {},
    proprietaryAttributeValues: {},
    installationDate: {},
    buildProjectId: {},
    buildId: {},
    buildConfidentiality: {},
    buildSerialNumber: {},
    productGroupDescription: {},
    productFamilyDescription: {},
    productSeriesDescription: {},
    sellableProductModelDescription: {},
    productName: {},
    productDescription: {},
    productLifeCycleStates: {},
    productOriginalSource: {},
    productHierarchyCode: {},
    primaryCommodity: {},
    siteOperatingStatus: {},
    buildEndOfSupportDate: {},
    buildEndOfFullSupportDate: {},
    customerId: {},
    customerName: {},
    customerSalesAccountName: {},
    customerRole: {},
    customerCVCCategory: {},
    customerSiteAccountManager: {},
    locationNickname: {},
    buildLocationType: {},
    documentName: {},
    documentId: {},
    documentEffectiveDate: {},
    documentLocationSiteId: {},
    documentLocationType: {},
    documentLocationStatus: {},
    documentLocationSiteDescription: {},
    documentAuthor: {},
    documentDocTypeCode: {},
    documentIsDeliverable: {},
    documentFileName: {},
    buildOem: {},
    documentLocationInformationLifecycle: {},
    buildConstructionYear: {},
    buildDeliveryDate: {},
    buildGlobalProjectId: {},
    buildSource: {},
    buildTagNumber: {},
    buildWarrantyStartDate: {},
    locationDevelopmentState: {},
    buildApplication: {},
    buildCreatedDate: {},
    documentClassification: {},
    documentFunctionalLocationId: {},
    documentSource: {},
    locationState: {},
  },
  optionsLoaded: false,
  latestAction: "",
  removedColumns: [],
  tableSort: {
    searchField: "",
    desc: false,
  },
  tableFilters: [],
  groupBy: "",
  loading: false,
  termsAndConditionsAccepted: false,
  exportColumns: [],
  advancedSearchCancelled: false,
  advancedSearchProgress: {
    totalCount: null,
    resultCount: 0,
  },
  editFilter: {},
  isExporting: false,
  exportFileName: "",
  exportFiles: {},
  downloadsMenuActive: false,
};

const useSearchStore = create<searchStoreState>((set, get) => ({
  ...initialState,

  loadRecentQuery: (query: Models.UserSearch) => {
    const queryColumns: Models.SearchResultColumn[] = query?.activeColumns;
    let newTableColumns = [];
    if (queryColumns?.length !== 0) {
      newTableColumns = _.unionBy(
        queryColumns,
        _.map(get().tableColumns, (tc) =>
          _.assign({}, tc, {
            active: false,
          })
        ),
        "displayName"
      );
    }

    set({
      filters: _.assign({}, initialState.filters, query?.filters),
      searchIndex: query?.searchIndex,
      tableColumns: newTableColumns,
    });
  },

  getAdvancedSearchOptions: async () => {
    const isOptionsLoaded = get().optionsLoaded;
    if (isOptionsLoaded) {
      return;
    }
    const fetchedOptions = await AzureApi.fetchAdvancedSearchOptions();
    const finalOptions: any = _.mapValues(
      fetchedOptions,
      (settingField: Models.SearchSettingField) => {
        if (!settingField) {
          return {};
        }
        const sortedOptions: any[] = _.sortBy(settingField.options);
        return {
          ...settingField,
          options: _.uniqBy(sortedOptions, "value"),
        };
      }
    );
    const columns: Models.SearchResultColumn[] = searchHelpers.optionsToColumns(
      fetchedOptions,
      get().searchIndex
    );
    set({
      options: finalOptions,
      optionsLoaded: true,
      tableColumns: columns,
    });
    const searchIndex: string = get().searchIndex;
    if (!!searchIndex) {
      const columns: Models.SearchResultColumn[] = setColumnDefaultValues(
        get(),
        searchIndex
      );
      set({ tableColumns: columns });
    }
  },

  setAdvancedSearchIndex: (newSearchIndex) => {
    const state = get();
    set({
      searchIndex: newSearchIndex,
      results: initialState.results,
      latestAction: initialState.latestAction,
      filters: initialState.filters,
      removedColumns: initialState.removedColumns,
      exportColumns: initialState.exportColumns,
      editFilter: initialState.editFilter,
    });
    const columns: Models.SearchResultColumn[] = setColumnDefaultValues(
      get(),
      get().searchIndex
    );
    _.forEach(columns, (c: Models.SearchResultColumn) => {
      c.order = getColumnSortValue(get().searchIndex, c);
    });
    set({ tableColumns: columns });
  },

  setAdvancedSearchEditMode: () =>
    set((state) => ({
      searchIndex: state.searchIndex === "" ? "builds" : state.searchIndex,
      isEditMode: !state.isEditMode,
    })),

  setSearchFilter: (payload: Models.SearchFilterPayload) => {
    const state = get();
    const { setFilters } = get();

    const tableColumns1: Models.SearchResultColumn[] =
      getTableColumnsByResultView(state, true);
    let searchField: string = payload?.filter;

    if (payload?.filter === "engineeringClassAttributeNames") {
      searchField = payload.field.value;
    }

    const col: Models.SearchResultColumn = _.find(
      tableColumns1,
      (tc) =>
        tc.searchField?.toLowerCase() === searchField?.toLowerCase() ||
        tc.displayName === payload.label
    );
    if (!col) {
      if (payload?.filter === "engineeringClassAttributeNames") {
        const newCol: Models.SearchResultColumn = {
          searchField: searchField,
          displayName: payload.label,
          active: true,
          columnFieldInResultViews: [Models.SearchSettingGroupType.Build],
          isColumn: true,
          searchResultOrders: [],
          filterable: false,
          classAttribute: true,
        };
        set((state) => ({ tableColumns: [...state.tableColumns, newCol] }));
      }
    } else {
      if (!col.active) {
        col.active = true;
        const columns: Models.SearchResultColumn[] = [...state.tableColumns];

        // SearchResultColumn collection may contain multiple columns with same searchField.
        // filterGroupOrder ensures that we find correct column
        const colIndex: number = _.findIndex(
          columns,
          (tc) =>
            tc.searchField === col.searchField &&
            tc.filterGroupOrder == col.filterGroupOrder
        );

        if (colIndex > -1) {
          columns[colIndex] = col;
        }
        set({ tableColumns: columns });
      }
    }
    setFilters(get(), payload);
  },

  changeSearchFilterOperator: (payload: Models.SearchFilterPayload) => {
    const { filters } = get();
    const type: Models.SearchSettingElementType = payload.elementType;
    const filterName: string = payload.filter;
    const newFilters: Models.SearchFilters = _.assign({}, filters);
    const newField: Models.SearchFilterValue = payload.field;

    if (!payload.operator) {
      return;
    }

    newField.operator = payload.operator;

    const indexOfFilter: number = _.findIndex(
      newFilters[filterName],
      (f: Models.SearchFilterValue) => _.isEqual(f.value, newField.value)
    );
    if (indexOfFilter > -1) {
      newFilters[filterName][indexOfFilter] = newField;
    } else {
      newFilters[filterName] = newFilters[filterName].concat(newField);
    }
    set({ filters: newFilters });
  },

  hideFilters: (field: Models.SearchSettingField) => {
    const state = get();
    const newOptions: Models.SearchOptions = _.assign({}, state.options);

    _.forEach(_.keys(newOptions), (key) => {
      if (newOptions[key].columnHeaderLabel === field.columnHeaderLabel) {
        newOptions[key].hidden = !newOptions[key].hidden;
        return false;
      }
    });
    set({ options: newOptions });
  },

  saveHiddenFilters: async () => {
    const { options, setAdvancedSearchEditMode } = get();
    var hiddenOptionNames: string[] = _.map(
      _.filter(_.values(options), "hidden"),
      "columnHeaderLabel"
    );

    // NO API PRESENT FOR SAVING HIDDEN FILTERS AS OF NOW SO COMMENTING THE API CALL, CAN BE USED LATER IF API IS CREATED
    // await AzureApi.saveHiddenFilters(hiddenOptionNames);
    setAdvancedSearchEditMode();
  },

  removeSearchFilter: (payload: Models.SearchFilterPayload) => {
    const { filters } = get();
    const type: Models.SearchSettingElementType = payload.elementType;
    const filterName: string = payload.filter;
    const newFilters: Models.SearchFilters = _.assign({}, filters);
    const newField: Models.SearchFilterValue = payload.field;

    if (type === Models.SearchSettingElementType.Text) {
      newFilters[filterName] = _.filter(
        newFilters[filterName],
        (f: Models.SearchFilterValue) => f.value !== newField.value
      );
    } else if (
      type === Models.SearchSettingElementType.Select ||
      type == Models.SearchSettingElementType.SelectWithOperators
    ) {
      newFilters[filterName] = _.filter(
        newFilters[filterName],
        (f: Models.SearchFilterValue) => f.value !== newField.value
      );
    } else if (type === Models.SearchSettingElementType.SelectFromOptions) {
      newFilters[filterName] = _.filter(
        newFilters[filterName],
        (f: Models.SearchFilterValue) => !_.isEqual(f, newField)
      );
    } else if (type === Models.SearchSettingElementType.TextWithOperators) {
      newFilters[filterName] = _.filter(
        newFilters[filterName],
        (f: Models.SearchFilterValue) => !_.isEqual(f, newField)
      );
    }
    set({ filters: newFilters });
  },

  resetAdvancedSearchFilters: () => {
    const state = get();
    const columns: Models.SearchResultColumn[] = setColumnDefaultValues(
      state,
      state.searchIndex
    );
    set({
      tableColumns: columns,
      results: {},
      latestAction: "",
      filters: initialState.filters,
      removedColumns: [],
      exportColumns: [],
      groupBy: "",
      tableSort: {
        searchField: "",
        desc: false,
      },
      tableFilters: [],
      termsAndConditionsAccepted: false,
    });
  },

  setFilters: (state, payload) => {
    const elementType: Models.SearchSettingElementType = payload.elementType;
    const singleValue: boolean = payload.singleValue
      ? payload.singleValue
      : false;
    const filterName: string = payload.filter;
    const newFilters: Models.SearchFilters = _.assign({}, state.filters);
    const newField: Models.SearchFilterValue = _.assign({}, payload.field);
    if (
      !payload.isComparable &&
      newField.comparisonOperator !== "between" &&
      newField.compareFrom !== undefined
    ) {
      newField.compareFrom = "";
    }

    if (singleValue) {
      newFilters[filterName] = [newField];
    } else if (
      elementType === Models.SearchSettingElementType.Select ||
      elementType === Models.SearchSettingElementType.SelectWithOperators
    ) {
      newFilters[filterName] = newFilters[filterName].concat(newField);
    } else {
      let indexOfFilter: number = _.findIndex(
        newFilters[filterName],
        (f: Models.SearchFilterValue) => _.isEqual(f, newField)
      );

      if (elementType === Models.SearchSettingElementType.SelectFromOptions) {
        indexOfFilter = _.findIndex(
          newFilters[filterName],
          (f: Models.SearchFilterValue) => _.isEqual(f.value, newField.value)
        );
      }

      if (indexOfFilter > -1) {
        newFilters[filterName][indexOfFilter] = newField;
      } else {
        newFilters[filterName] = newFilters[filterName].concat(newField);
      }
    }
    set({ filters: newFilters });
  },

  editSearchFilter: (payload: Models.SearchFilterPayload) => {
    set({ editFilter: payload });
  },

  setAdvancedSearchLoading: (value: boolean) => {
    set({ loading: value });
  },

  setAdvancedSearchCancelled: (value: boolean) => {
    set({ advancedSearchCancelled: value });
  },

  doAdvancedSearch: async (silent?: boolean) => {
    const state = get();
    set({
      advancedSearchCancelled: false,
      advancedSearchProgress: {
        totalCount: null,
        resultCount: 0,
      },
    });
    let advancedSearchPayload: Models.AdvancedSearchApiPayload =
      getPayload(state);
    const exportCols: Models.ExportColumn[] = _.map(
      advancedSearchPayload.columns,
      (col) => {
        const newCol: Models.ExportColumn = {
          searchField: col.searchField,
          displayName: col.displayName,
          order: col.order,
        };
        return newCol;
      }
    );
    set({ exportColumns: exportCols });
    advancedSearchPayload.showLoading = !silent;
    advancedSearchPayload.removedColumns = get().removedColumns;

    const user: Models.User = userStore.getState().user;
    const marketAreaSettings: Models.MarketAreaSettings =
      useMarketAreaStore.getState().marketAreaSettings;
    let activeMarketArea: string = marketAreaSettings.activeMarketArea;
    if (user.isAdminUser) {
      advancedSearchPayload.adminMarketArea = activeMarketArea;
    } else {
      advancedSearchPayload.adminMarketArea = "";
    }

    // Fetch result count first for progress - this can be removed if it takes too long, during testing it was <1s
    await get().fetchResultCount(advancedSearchPayload);

    let fetchedResults: Models.DetailCollection = null;
    // Hard limit on loop is 150 000 results. Azure Search maximum result count is 100 000 results.
    for (let page: number = 0; page * pageSize <= 150000; page++) {
      advancedSearchPayload = {
        ...advancedSearchPayload,
        top: pageSize,
        skip: page * pageSize,
      };
      const partResults: Models.DetailCollection =
        await AzureApi.fetchAdvancedSearchResults(advancedSearchPayload);
      fetchedResults = combineResults(fetchedResults, partResults);

      // Set total count and result count for progress bar during advanced search
      if (partResults.totalCount !== null) {
        set({
          advancedSearchProgress: {
            totalCount: partResults.totalCount,
            resultCount:
              fetchedResults[advancedSearchPayload.searchIndex].length ?? 0,
          },
        });
      }
      // Reached end of search results, or advanced search was cancelled
      if (
        partResults[advancedSearchPayload.searchIndex]?.length < pageSize ||
        get().advancedSearchCancelled
      ) {
        break;
      }
    }

    if (get().advancedSearchCancelled) {
      return;
    }

    if (
      silent &&
      fetchedResults[advancedSearchPayload.searchIndex].length === 0
    ) {
      return;
    }

    set({
      results: fetchedResults,
      termsAndConditionsAccepted: false,
      latestAction: "search",
    });

    if (!silent) {
      setTimeout(() => {
        const element: any = document.querySelector(".AdvancedSearch__results");
        if (element) {
          window.scroll(0, element.offsetTop + 50);
        }
      }, 500);
    }
  },

  fetchResultCount: async (advancedSearchPayload): Promise<void> => {
    advancedSearchPayload = { ...advancedSearchPayload, top: 0, skip: 0 };
    const resultCount: Models.DetailCollection =
      await AzureApi.fetchAdvancedSearchResults(advancedSearchPayload);
    set({
      advancedSearchProgress: {
        totalCount: resultCount.totalCount,
        resultCount: 0,
      },
    });
  },

  onToggleColumn: (columns) => {
    const tableColumnsNew: Models.SearchResultColumn[] = get().tableColumns;
    const arr: Models.SearchResultColumn[] = tableColumnsNew.filter(
      (c) => columns.indexOf(c.displayName) >= 0
    );
    const activeColumns: Models.SearchResultColumn[] = arr;
    const group: number = activeColumns[0].group;
    _.forEach(
      tableColumnsNew.filter((c) => c.group === group),
      (c) => {
        c.active = false;
      }
    );
    const columnMap = _.keyBy(tableColumnsNew, "searchField");
    _.forEach(activeColumns, (c) => {
      const col = columnMap[c.searchField];
      if (col) col.active = true;
    });
    set({ tableColumns: tableColumnsNew });
  },

  onClearGroupColumns: (group: number) => {
    const newColumns: Models.SearchResultColumn[] = [...get().tableColumns];
    newColumns.forEach((c) => {
      if (c.group === group) c.active = false;
    });
    set({ tableColumns: newColumns });
  },

  onAddColumn: (columns: Models.ReactTableFilter[]) => {
    const tableColumns: Models.SearchResultColumn[] =
      getTableColumnsByResultView(get(), false);
    let columnsAdded: boolean = false;

    const newColumns: Models.ReactTableFilter[] = columns.filter(
      (c) => tableColumns.map((c) => c.searchField).indexOf(c.value) < 0
    );
    const arr: Models.SearchResultColumn[] = tableColumns.filter(
      (c) => columns.map((c) => c.value).indexOf(c.searchField) >= 0
    );

    _.forEach(newColumns, (column) => {
      const newCol: Models.SearchResultColumn = {
        searchField: column.value,
        displayName: column.label,
        active: true,
        columnFieldInResultViews: [Models.SearchSettingGroupType.Build],
        isColumn: true,
        filterable: false,
        classAttribute: true,
        order: tableColumns.length,
        searchResultOrders: [],
        group: 0,
      };
      arr.push(newCol);
      columnsAdded = true;
      set((state) => ({ tableColumns: [...state.tableColumns, newCol] }));
    });

    const activeColumns: Models.SearchResultColumn[] = arr;
    const newTableColumns = [...get().tableColumns];
    _.forEach(
      newTableColumns.filter((c) => c.classAttribute === true),
      (c) => {
        c.active = false;
      }
    );

    const columnMap = _.keyBy(newTableColumns, "searchField");
    _.forEach(activeColumns, (c) => {
      const col = columnMap[c.searchField];
      if (col) col.active = true;
    });

    set({ tableColumns: newTableColumns });

    if (columnsAdded) {
      get().doAdvancedSearch(true);
    }
  },

  onClearClassColumns: () => {
    const newColumns = get().tableColumns;
    newColumns.forEach((column) => {
      if (column.classAttribute === true) column.active = false;
    });
    set({ tableColumns: newColumns });
  },

  exportToExcel: async () => {
    set({ loading: true });
    const advancedSearchPayload: Models.AdvancedSearchApiPayload =
      getExportPayload(get());
    const user: Models.User = userStore.getState().user;
    const marketAreaSettings: Models.MarketAreaSettings =
      useMarketAreaStore.getState().marketAreaSettings;
    let activeMarketArea: string = marketAreaSettings.activeMarketArea;
    if (user.isAdminUser) {
      advancedSearchPayload.adminMarketArea = activeMarketArea;
    } else {
      advancedSearchPayload.adminMarketArea = "";
    }
    let exportFileId: string = await AzureApi.exportExcel(
      advancedSearchPayload
    );
    exportFileId = exportFileId.replace(/"/g, "");
    let newId = exportFileId;
    let newFile: Models.FileExport;
    const exportFiles = get().exportFiles;
    if (!(Object.keys(exportFiles).indexOf(newId) > -1)) {
      newFile = {
        id: newId,
        status: Models.FileExportStatus.Created,
        uri: undefined,
        visible: true,
      };
    } else {
      newFile = {
        ...exportFiles[newId],
        visible: true,
      };
    }
    set({
      exportFiles: { ...exportFiles, [newId]: newFile },
      loading: false,
      downloadsMenuActive: true,
      latestAction: "export",
    });
  },

  setAdvancedSearchTermsAndConditions: (value: boolean) => {
    set({ termsAndConditionsAccepted: value });
  },

  toggleDownloadsMenu: (show: boolean) => {
    set({ downloadsMenuActive: show });
  },

  updateAdvancedSearchexportFile: (action: Models.FileExportActionPayload) => {
    const _exportFiles = get().exportFiles;
    // Track all export files, set visibility to false
    let statusKey = action.status as keyof typeof FileExportStatus;
    if (!(Object.keys(_exportFiles).indexOf(action.id) > -1)) {
      let update: Models.FileExport = {
        id: action.id,
        status: FileExportStatus[statusKey],
        uri: action.uri,
        visible: false,
      };
      return {
        ..._exportFiles,
        [update.id]: update,
      };
    }

    let update: Models.FileExport = _exportFiles[action.id];
    update = {
      ...update,
      status: FileExportStatus[statusKey],
      uri: action.uri,
    };
    if (update.visible && update.id === action.id) {
      if (
        statusKey &&
        FileExportStatus[statusKey] === FileExportStatus.Failed
      ) {
        alert(
          "Excel export failed: " + action.message
            ? action.message
            : "Unknown reason"
        );
      }
    }
    set({
      exportFiles: {
        ...get().exportFiles,
        [update.id]: update,
      },
    });
  },
}));

export default useSearchStore;
